import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Input,
  Switch,
  Row,
  Col,
  Typography,
  Alert,
  Space,
  Divider,
} from "antd";
import {
  HomeOutlined,
  EditOutlined,
  BankOutlined,
  PhoneOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

const { Text } = Typography;
const { TextArea } = Input;

interface BillingAddressFormProps {
  form: any;
  onBillingAddressChange?: (billingAddress: any) => void;
  onValidationChange?: (isValid: boolean) => void;
  companyData?: any; // Company bilgileri (fallback için)
}

const BillingAddressForm: React.FC<BillingAddressFormProps> = ({
  form,
  onBillingAddressChange,
  onValidationChange,
  companyData,
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [useCompanyAddress, setUseCompanyAddress] = useState(true);
  const [customBillingAddress, setCustomBillingAddress] = useState<any>(null);
  const [customForm] = Form.useForm(); // Kendi form instance'ı

  // Şirket adresi verilerinin geçerliliğini kontrol et
  const isCompanyAddressValid = useCallback(() => {
    if (!companyData) return false;
    // Gerekli alanların dolu olup olmadığını kontrol et
    return (
      companyData.name &&
      companyData.address &&
      companyData.city &&
      companyData.district &&
      companyData.taxNumber &&
      companyData.taxOffice &&
      companyData.phone &&
      companyData.email
    );
  }, [companyData]);

  // Company bilgilerinden default billing address oluştur
  const getDefaultBillingAddress = useCallback(() => {
    const company = companyData;
    if (!company) return null;

    return {
      alias: "Şirket Adresi",
      contactName:
        `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
        company.name,
      companyName: company.name || "Şirket adı bulunamadı",
      address: company.address || "Adres bilgisi bulunamadı",
      city: company.city || "İstanbul", // Default city - company'de city alanı yoksa
      district: company.district || "",
      zipCode: "34000", // Default zip code
      country: "Turkey",
      taxNumber: company.taxNumber || "",
      taxOffice: company.taxOffice || "",
      phone: company.phone || user?.phone || "",
      email: company.email || user?.email || "",
    };
  }, [companyData, user]);

  // Billing address değişikliklerini ve geçerlilik durumunu parent'a bildir
  useEffect(() => {
    let billingAddress = null;
    let isValid = false;

    if (useCompanyAddress) {
      billingAddress = getDefaultBillingAddress();
      isValid = isCompanyAddressValid();
    } else {
      billingAddress = customBillingAddress;
      // customBillingAddress'in de geçerli olup olmadığını kontrol etmemiz gerekir.
      // Şimdilik, custom formun geçerliliği `handleCustomAddressChange` içinde yönetiliyor.
      // Formun anlık geçerlilik durumunu kontrol etmek için:
      customForm
        .validateFields()
        .then(() => {
          if (onValidationChange) onValidationChange(true);
        })
        .catch(() => {
          if (onValidationChange) onValidationChange(false);
        });
      // Bu useEffect'in her render'da çalışmasını önlemek için bağımlılıkları kontrol etmeliyiz.
      // Şimdilik bu şekilde bırakıyorum ama daha iyi bir çözüm bulunabilir.
      isValid =
        customBillingAddress &&
        Object.values(customBillingAddress).every((v) => v !== "");
    }

    if (onBillingAddressChange && billingAddress) {
      onBillingAddressChange(billingAddress);
    }
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [
    useCompanyAddress,
    customBillingAddress,
    onBillingAddressChange,
    onValidationChange,
    isCompanyAddressValid,
    getDefaultBillingAddress,
    customForm,
  ]);

  // Custom billing address form değişikliklerini takip et
  const handleCustomAddressChange = async () => {
    try {
      // Formun geçerliliğini kontrol et
      await customForm.validateFields();

      // Kendi form instance'ından tüm değerleri al
      const formValues = customForm.getFieldsValue();

      // ContactName'i otomatik olarak user'dan al
      const contactName =
        `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
        "Kullanıcı";

      const newCustomAddress = {
        alias: formValues.customAlias || "Özel Fatura Adresi",
        contactName: contactName,
        companyName: formValues.customCompanyName || "",
        address: formValues.customAddress || "",
        city: formValues.customCity || "",
        district: formValues.customDistrict || "",
        zipCode: formValues.customZipCode || "",
        country: "Turkey",
        taxNumber: formValues.customTaxNumber || "",
        taxOffice: formValues.customTaxOffice || "",
        phone: formValues.customPhone || "",
        email: formValues.customEmail || "",
      };

      setCustomBillingAddress(newCustomAddress);
      if (onValidationChange) onValidationChange(true);
    } catch (error) {
      // Form geçerli değilse
      if (onValidationChange) onValidationChange(false);
    }
  };

  const defaultBilling = getDefaultBillingAddress();

  return (
    <Card
      title={
        <Space>
          <HomeOutlined />
          <span>Fatura Adresi</span>
        </Space>
      }
      style={{ marginBottom: "24px", padding: "16px" }}
    >
      {/* Mevcut Şirket Adresi Gösterimi */}
      {defaultBilling && (
        <div style={{ marginBottom: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <Text strong style={{ fontSize: "16px" }}>
              <BankOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
              Kayıtlı Şirket Bilgileri
            </Text>
            <Switch
              checked={useCompanyAddress}
              onChange={setUseCompanyAddress}
              checkedChildren="Kullan"
              unCheckedChildren="Özel Adres"
            />
          </div>

          <Card
            size="small"
            style={{
              background: useCompanyAddress ? "rgb(245, 240, 254)" : "#f5f5f5",
              border: useCompanyAddress
                ? "2px solid rgb(181, 44, 223)"
                : "1px solid #d9d9d9",
              padding: "16px",
            }}
          >
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Text type="secondary">Şirket Adı:</Text>
                <br />
                <Text strong>
                  {companyData?.name || "Şirket adı bulunamadı"}
                </Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text type="secondary">Fatura Sorumlusu:</Text>
                <br />
                <Text strong>{defaultBilling.contactName}</Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text type="secondary">Vergi No:</Text>
                <br />
                <Text strong>
                  {defaultBilling.taxNumber || "Belirtilmemiş"}
                </Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text type="secondary">Vergi Dairesi:</Text>
                <br />
                <Text strong>
                  {defaultBilling.taxOffice || "Belirtilmemiş"}
                </Text>
              </Col>
              <Col xs={24}>
                <Text type="secondary">Adres:</Text>
                <br />
                <Text strong>{defaultBilling.address}</Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text type="secondary">Şehir / İlçe:</Text>
                <br />
                <Text strong>
                  {defaultBilling.city} / {defaultBilling.district}
                </Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text type="secondary">Telefon:</Text>
                <br />
                <Text strong>{defaultBilling.phone || "Belirtilmemiş"}</Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text type="secondary">E-posta:</Text>
                <br />
                <Text strong>{defaultBilling.email || "Belirtilmemiş"}</Text>
              </Col>
            </Row>
          </Card>
        </div>
      )}

      {/* Özel Fatura Adresi Formu */}
      {!useCompanyAddress && (
        <div>
          <Divider orientation="left">
            <Space>
              <EditOutlined />
              <span>Özel Fatura Adresi</span>
            </Space>
          </Divider>

          <Alert
            message="Özel Fatura Adresi"
            description={
              <div>
                <Text>
                  Faturanızın farklı bir adrese gönderilmesini istiyorsanız
                  aşağıdaki bilgileri doldurun.
                </Text>
                <br />
                <Text strong style={{ color: "#1890ff" }}>
                  Fatura Sorumlusu:{" "}
                  {`${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
                    "Kullanıcı"}
                </Text>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: "20px" }}
          />

          <Form
            form={customForm}
            layout="vertical"
            onValuesChange={handleCustomAddressChange}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24}>
                <Form.Item
                  name="customCompanyName"
                  label="Şirket Adı"
                  rules={[{ required: true, message: "Şirket adı gereklidir" }]}
                >
                  <Input
                    prefix={<BankOutlined />}
                    placeholder="ABC Teknoloji Ltd. Şti."
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="customTaxNumber"
                  label="Vergi Numarası"
                  rules={[
                    { required: true, message: "Vergi numarası gereklidir" },
                  ]}
                >
                  <Input placeholder="1234567890" maxLength={11} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="customTaxOffice"
                  label="Vergi Dairesi"
                  rules={[
                    { required: true, message: "Vergi dairesi gereklidir" },
                  ]}
                >
                  <Input placeholder="Kadıköy Vergi Dairesi" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="customAddress"
                  label="Adres"
                  rules={[{ required: true, message: "Adres gereklidir" }]}
                >
                  <TextArea placeholder="Tam adres bilgisi" rows={3} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="customCity"
                  label="Şehir"
                  rules={[{ required: true, message: "Şehir gereklidir" }]}
                >
                  <Input placeholder="İstanbul" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="customDistrict"
                  label="İlçe"
                  rules={[{ required: true, message: "İlçe gereklidir" }]}
                >
                  <Input placeholder="Kadıköy" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="customZipCode"
                  label="Posta Kodu"
                  rules={[{ required: true, message: "Posta kodu gereklidir" }]}
                >
                  <Input placeholder="34000" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="customPhone"
                  label="Telefon"
                  rules={[
                    { required: true, message: "Telefon numarası gereklidir" },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="0555 123 45 67"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="customEmail"
                  label="E-posta Adresi"
                  rules={[
                    { required: true, message: "E-posta gereklidir" },
                    {
                      type: "email",
                      message: "Geçerli bir e-posta adresi girin",
                    },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="fatura@sirket.com"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      )}

      {/* Bilgilendirme */}
      {/* <Alert
        message={
          <Space>
            <InfoCircleOutlined />
            <span>Fatura Bilgisi</span>
          </Space>
        }
        description="Faturanız seçtiğiniz adrese e-posta olarak gönderilecektir. Fiziki fatura gönderimi bulunmamaktadır."
        type="info"
        showIcon={false}
        style={{ marginTop: '16px' }}
      /> */}
    </Card>
  );
};

export default BillingAddressForm;
